import { useSyncExternalStore } from 'react';

/**
 * "Auf den Startbildschirm" (W9, 1.16): Chrome, Edge and Android offer installing a web app with
 * the `beforeinstallprompt` event. It fires early, often before "Heute" is loaded – so this module
 * is imported by main.tsx and keeps the offer until a button uses it. iPhone/iPad have no such
 * event; there PushPrompt explains "Teilen → Zum Home-Bildschirm".
 */
type InstallEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let offer: InstallEvent | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // keep the browser's own mini bar away – the card on "Heute" asks at a calmer moment
    e.preventDefault();
    offer = e as InstallEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    offer = null;
    emit();
  });
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/** True while the browser offers installing Larovi Home on this device. */
export function useInstallOffer(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => offer !== null,
    () => false,
  );
}

/** Opens the browser's install dialog; an offer can be used only once. */
export async function install(): Promise<boolean> {
  const e = offer;
  if (!e) return false;
  offer = null;
  emit();
  await e.prompt();
  return (await e.userChoice).outcome === 'accepted';
}
